import React from 'react';
import { ArrowRight, Code2, Smartphone, Cpu, Layers, BarChart3, Cloud, Sparkles } from 'lucide-react';
import { servicesData } from '../../data/servicesData';
import { ServiceItem } from '../../types';

interface ServicesGridProps {
  onSelectService: (slug: string) => void;
}

export const ServicesGrid: React.FC<ServicesGridProps> = ({ onSelectService }) => {
  const getIcon = (name: string, accent: string) => {
    const iconClass = "w-6 h-6 text-white";
    switch (name) {
      case 'Code2':
        return <Code2 className={iconClass} />;
      case 'Smartphone':
        return <Smartphone className={iconClass} />;
      case 'Cpu':
        return <Cpu className={iconClass} />;
      case 'Layers':
        return <Layers className={iconClass} />;
      case 'Cloud':
        return <Cloud className={iconClass} />;
      case 'BarChart3':
      default:
        return <BarChart3 className={iconClass} />;
    }
  };

  const getAccentBadge = (color: ServiceItem['accentColor']) => {
    switch (color) {
      case 'purple':
        return 'bg-[#7B2EFF] shadow-glow-purple';
      case 'blue':
        return 'bg-[#159DE8] shadow-glow-blue';
      case 'orange':
        return 'bg-[#FF851B] shadow-glow-orange';
      case 'green':
        return 'bg-[#00C853] shadow-md';
      case 'coral':
      default:
        return 'bg-[#FF5A36] shadow-glow-orange';
    }
  };

  const getBorderHover = (color: ServiceItem['accentColor']) => {
    switch (color) {
      case 'purple':
        return 'hover:border-[#7B2EFF]/60 group-hover:shadow-glow-purple';
      case 'blue':
        return 'hover:border-[#159DE8]/60 group-hover:shadow-glow-blue';
      case 'orange':
        return 'hover:border-[#FF851B]/60 group-hover:shadow-glow-orange';
      case 'green':
        return 'hover:border-[#00C853]/60';
      case 'coral':
      default:
        return 'hover:border-[#FF5A36]/60 group-hover:shadow-glow-orange';
    }
  };

  return (
    <section id="services-section" className="bg-[#050A13] py-20 border-b border-[#1b2f4a] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
          <div className="inline-flex items-center gap-2">
            <span className="h-px w-8 bg-[#FF851B]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF851B]">
              WHAT WE DO
            </span>
            <span className="h-px w-8 bg-[#FF851B]" />
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight font-display">
            Digital Solutions That Drive Growth
          </h2>

          <p className="text-[#D5DFEC] text-sm sm:text-base leading-relaxed">
            From intelligent software engineering to digital customer acquisition, we help businesses build, launch, and scale their digital presence.
          </p>
        </div>

        {/* 6-Card Service Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesData.map((service) => {
            return (
              <div
                key={service.id}
                onClick={() => onSelectService(service.slug)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectService(service.slug);
                  }
                }}
                tabIndex={0}
                role="button"
                className={`p-7 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] transition-all duration-300 cursor-pointer flex flex-col justify-between group hover:-translate-y-1 ${getBorderHover(service.accentColor)}`}
              >
                <div>
                  {/* Service Icon */}
                  <div className="flex items-center justify-between mb-5">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getAccentBadge(service.accentColor)}`}>
                      {getIcon(service.iconName, service.accentColor)}
                    </div>
                    <span className="text-xs font-mono uppercase tracking-widest text-[#8FA0B8]/60 group-hover:text-white transition-colors">
                      {service.id.toUpperCase()}
                    </span>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-lg font-bold text-white mb-2.5 group-hover:text-[#FF851B] transition-colors font-display">
                    {service.title}
                  </h3>

                  <p className="text-xs sm:text-sm text-[#8FA0B8] leading-relaxed mb-6 group-hover:text-[#D5DFEC] transition-colors">
                    {service.shortDescription}
                  </p>
                </div>

                {/* Card CTA Link */}
                <div className="pt-4 border-t border-[#1b2f4a]/70 flex items-center justify-between">
                  <span className="text-xs font-semibold text-white group-hover:text-[#159DE8] transition-colors flex items-center gap-1.5">
                    <span>Explore Solutions</span>
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform text-[#FF851B]" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};
